import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import Column from '@components/Column';
import CustomBarcode from '@components/CustomBarcode';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

declare module '@stackflow/config' {
    interface Register {
        PrepaidCardBarcodeBottomSheet: {
            prepaidCardId: string;
        };
    }
}

/** 내 선불카드 바코드 */
const PrepaidCardBarcodeBottomSheet: ActivityComponentType<'PrepaidCardBarcodeBottomSheet'> = ({ params }) => {
    const { prepaidCardId } = params;
    const { t } = useTranslation('prepaidCard');
    // MyPrepaidCardActivity 가 조회한 목록 캐시를 그대로 쓴다.
    const { data: card } = useQuery({
        ...userPrepaidCardQueries.list(),
        select: (data) => data.cards.find((it) => it.id === prepaidCardId),
        staleTime: Infinity,
        refetchOnMount: false,
    });
    const expiredLabel = card?.expiredAt
        ? t('barcode.until', {
            date: dayjs(card.expiredAt.replace(/\./g, '-')).format('YYYY.MM.DD'),
        })
        : null;

    return (
        <BottomSheet>
            <Header>
                <Text size='title' fontWeight='bold'>
                    {t('barcode.title')}
                </Text>
            </Header>
            {card && (
                <Container fullWidth spacing={4}>
                    <Row fullWidth spacing={4} align='center'>
                        <CardInfo spacing={1}>
                            <Text size='body-long' fontWeight='regular'>
                                {card.name}
                            </Text>
                            {expiredLabel && (
                                <Text size='body-short' fontWeight='regular' color={colors.textSecondary}>
                                    {expiredLabel}
                                </Text>
                            )}
                        </CardInfo>
                        <Balance size='body-short' fontWeight='medium'>
                            {t('barcode.balance', { amount: card.balance.toLocaleString() })}
                        </Balance>
                    </Row>
                    <BarcodeArea fullWidth spacing={1} align='center'>
                        <CustomBarcode value={card.barcodeNo || ''} height={46} width="calc(100% - 64px)" />
                        <Text size='body-short' fontWeight='regular' align='center'>
                            {formatBarcodeNo(card.barcodeNo)}
                        </Text>
                    </BarcodeArea>
                </Container>
            )}
        </BottomSheet>
    );
};

function formatBarcodeNo(barcodeNo?: string) {
    if (!barcodeNo) return '';
    const digits = barcodeNo.replace(/\s/g, '');
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

const Header = styled.div`
    align-items: center;
    display: flex;
    height: 64px;
    justify-content: center;
`;

const Container = styled(Column)`
    padding: 0 var(--spacing-4) calc(var(--spacing-4) + var(--safe-area-bottom));
`;

const CardInfo = styled(Column)`
    flex: 1;
    min-width: 0;
`;

const Balance = styled(Text)`
    flex-shrink: 0;
    white-space: nowrap;
`;

const BarcodeArea = styled(Column)`
    border: 1px solid ${colors.gray2};
    border-radius: var(--radius-2);
    padding: var(--spacing-4) 0;
`;

export default PrepaidCardBarcodeBottomSheet;
